<script setup lang="ts">
import { type PropType, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import { caregiversApi, type CaregiverListItem } from '@/api/caregivers';
import type { AxiosError } from 'axios';
import { usePermissions } from '@/composables/usePermissions';

const { hasPermission } = usePermissions();

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch', 'success']);

const props = defineProps({
  caregiver: {
    type: Object as PropType<CaregiverListItem>,
    required: true,
  },
  closeButtonText: {
    type: String,
    default: 'Cerrar',
  },
});

const { handleSubmit } = useForm();

const { mutate, isError, error } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => caregiversApi.destroy(props.caregiver.id),
    onSuccess: () => {
      emit('refetch');
      emit('success');
      emit('close');
    },
  },
);

const submitButtonDisabled = ref(false);

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  mutate();
});

const errorMessage = computed(() => {
  if (isError.value && error.value?.response?.status) {
    const caregiverHasShiftsError = 422;
    if (error.value.response.status === caregiverHasShiftsError) {
      return t('caregiverDiscardForm.error');
    }

    return t('userSession.defaultError');
  }

  return '';
});
</script>
<template>
  <v-card
    v-if="hasPermission('caregivers', 'destroy')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ t('caregiverDiscardForm.title') }} {{ caregiver.fullName }}
    </v-card-title>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          {{ t('caregiverDiscardForm.confirmation') }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            :text="props.closeButtonText"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            color="red"
            type="submit"
            :disabled="submitButtonDisabled"
          >
            {{ t('caregiverDiscardForm.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ errorMessage }}
    </v-alert>
  </v-card>
</template>
